import { DotsHorizontalIcon } from "@radix-ui/react-icons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from "@libs/zapi-ui/shadcn";
import { MouseEvent, useEffect, useMemo, useState } from "react";
import { AppButton, Divider, FigmaTypography, LoadingSpinner } from "@libs/zapi-ui/custom";
import { useParams } from "react-router-dom";
import { splitFileName } from "@/zapi-frontend/shared/lib/splitFileName";
import $api from "@/zapi-frontend/shared/api/axios";
import {
  ArrowClockwiseIcon,
  TrashSimpleIcon,
  SparkleIcon,
  EyeIcon,
  XIcon,
  GlobeIcon,
  DownloadSimpleIcon
} from "@phosphor-icons/react";
import { parseSize } from "@/zapi-frontend/shared/lib/parseSize";
import { tryCatch } from "@/zapi-frontend/shared/lib/tryCatch";
import { getMalwareScanMessage } from "@/zapi-frontend/shared/lib/malwareScanError";
import { toast } from "sonner";
import { Bookmark, BookmarkCheck } from "lucide-react";
import { useOrganizationStore } from "@/zapi-frontend/stores/organization";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { TUsefulnessStatus } from "./hooks/useDocumentsUsefulness";
import { formatCharacterLength, formatCompressionRatio } from "@/zapi-frontend/shared/lib/formatCharacterLength";
import { TJuicedData, TSummarizedContent } from "@/zapi-frontend/shared/hooks/useDocuments";
import DocumentContentModal from "@/zapi-frontend/widgets/DocumentContentModal/DocumentContentModal";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";

type TFileItemProps = {
  name: string;
  size: number;
  documentId: number;
  webUrl?: string | null;
  uploadedBy?: string;
  deleteDocument: () => void;
  retryJuicing: () => Promise<void>;
  isDeleting?: boolean;
  status?: "pending" | "processed" | "error" | "processing";
  usefulness: TUsefulnessStatus;
  onChangeUsefulness: (usefulness: TUsefulnessStatus) => Promise<void>;
  refetchDocuments: () => Promise<void>;
  isLoadingDocumentsUsefulness?: boolean;
  contentType?: "FULL" | "SUMMARIZED";
  onToggleContentType?: (contentType: "FULL" | "SUMMARIZED") => Promise<void>;
  onSummarize?: () => Promise<void>;
  extractedContent?: TJuicedData;
  summarizedContent?: TSummarizedContent;
  fullContentDisplay?: string;
  summaryContentDisplay?: string;
  briefSummary?: string;
  fullWidth?: boolean;
};

export function FileItem(props: TFileItemProps) {
  const {
    fullWidth = false,
    uploadedBy,
    size,
    name,
    documentId,
    webUrl,
    deleteDocument,
    retryJuicing,
    isDeleting,
    status = "pending",
    usefulness,
    onChangeUsefulness,
    refetchDocuments,
    isLoadingDocumentsUsefulness = false,
    contentType = "FULL",
    onToggleContentType,
    onSummarize,
    fullContentDisplay = "",
    summaryContentDisplay = "",
    briefSummary
  } = props;
  const { id } = useParams();
  const { name: fileName, extension: fileType } = splitFileName(name);
  const { currentOrganizationId } = useOrganizationStore();
  const { role, accessMode } = useMeStore();
  const hasEdit = hasEditAccess(role, accessMode);
  const isWebLink = !!webUrl;

  const [imageUrl, setUrl] = useState<string | null>();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editedSummaryContent, setEditedSummaryContent] = useState(summaryContentDisplay);
  const [isSavingContent, setIsSavingContent] = useState(false);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);

  const [isSummaryOpen, setIsSummaryOpen] = useState(false); // NEW
  const summaryRegionId = useMemo(() => `file-${documentId}-brief-summary`, [documentId]);

  const fullContentDisplayLength = fullContentDisplay.length;
  const editedSummaryContentLength = editedSummaryContent.length;

  const hasSummarizedContent = summaryContentDisplay?.length > 0;

  const isContentAvailable = status === "processed" && fullContentDisplay !== "No extracted content available";

  useEffect(() => {
    setEditedSummaryContent(summaryContentDisplay);
  }, [summaryContentDisplay]);

  const projectId = useMemo(() => (typeof id === "string" ? id : ""), [id]);

  const sizeField = useMemo(() => {
    if (!size) return "";
    const { value, unit } = parseSize(size);
    return `${value}${unit}`;
  }, [size]);

  useEffect(() => {
    if (isWebLink) return;

    async function getUrl() {
      const config = currentOrganizationId ? { params: { organizationId: currentOrganizationId } } : undefined;
      const [response] = await tryCatch(
        $api.get<{ data: { url: string } }>(`/projects/${projectId}/documents/${documentId}`, config)
      );
      setUrl(response?.data.data.url ?? null);
    }

    getUrl();
  }, [documentId, projectId, currentOrganizationId, isWebLink]);

  async function handleDownload() {
    setDropdownOpen(false);
    setIsDownloading(true);
    const config = currentOrganizationId ? { params: { organizationId: currentOrganizationId } } : undefined;
    const [response, urlError] = await tryCatch(
      $api.get<{ data: { url: string } }>(`/projects/${projectId}/documents/${documentId}`, config)
    );
    const url = response?.data.data.url;
    if (urlError || !url) {
      setIsDownloading(false);
      toast.error(getMalwareScanMessage(urlError) ?? "Failed to download file");
      return;
    }

    const [blob, blobError] = await tryCatch(fetch(url).then((r) => r.blob()));
    if (blobError || !blob) {
      setIsDownloading(false);
      toast.error("Failed to download file");
      return;
    }

    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = name;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
    setIsDownloading(false);
  }

  function handleDelete() {
    setDropdownOpen(false);

    setTimeout(() => {
      deleteDocument();
    }, 10);
  }

  async function handleRetry(e: MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsRetrying(true);
    const [, error] = await tryCatch(retryJuicing());

    if (error) {
      console.error("Error retrying document processing:", error);
      toast.error("Failed to retry document processing. Please try again later.");
    }

    setIsRetrying(false);
  }

  const handleUsefulnessChange = async () => {
    const newUsefulness =
      usefulness === TUsefulnessStatus.USEFUL ? TUsefulnessStatus.USELESS : TUsefulnessStatus.USEFUL;

    try {
      await onChangeUsefulness(newUsefulness);
    } catch (error) {
      console.error("Error updating usefulness", error);
    }

    setDropdownOpen(false);
  };

  const handleSummarize = async () => {
    if (!onSummarize) return;

    setIsSummarizing(true);
    setDropdownOpen(false);

    try {
      await onSummarize();
      await refetchDocuments();
      toast.success("Document summarized successfully");
    } catch (error) {
      console.error("Error summarizing document:", error);
      toast.error("Failed to summarize document");
    } finally {
      setIsSummarizing(false);
    }
  };

  const handleViewContent = () => {
    setIsModalOpen(true);
    setDropdownOpen(false);
  };

  const handleSaveContent = async () => {
    setIsSavingContent(true);
    try {
      if (editedSummaryContent !== summaryContentDisplay) {
        await $api.put(`/projects/${projectId}/documents/${documentId}/content`, {
          content: editedSummaryContent,
          contentType: "summarized"
        });
        if (editedSummaryContent === "") await onToggleContentType?.("FULL");

        await refetchDocuments();
        toast.success("Summary updated successfully");
        setIsModalOpen(false);
      } else {
        toast.info("No changes to save");
        setIsModalOpen(false);
      }
    } catch (error) {
      console.error("Error saving summary:", error);
      toast.error("Failed to save summary");
    } finally {
      setIsSavingContent(false);
    }
  };

  const handleGenerateSummary = async () => {
    if (!onSummarize) return;
    setIsGeneratingSummary(true);
    await onSummarize();
    await refetchDocuments();
    setIsGeneratingSummary(false);
  };

  return (
    <>
      <div
        className="border-b border-innoveraZinc-150 w-full min-h-[62px] py-3"
        data-testid={`file-item-${documentId}`}
      >
        <div className="flex justify-between w-full">
          <div className="flex items-center space-x-2 rounded-sm px-1 max-w-[80%] sm:max-w-[60%]">
            <div className="flex flex-shrink-0 items-center justify-center w-7 h-9 border border-innoveraZinc-150 rounded-[5px] p-1 text-[8px] text-innoveraZinc-500">
              {isWebLink ? <GlobeIcon className="w-4 h-4" /> : fileType}
            </div>
            <div
              className={cn(
                "flex flex-col justify-center w-full max-w-[110px] sm:max-w-[140px]",
                fullWidth && "max-w-full sm:max-w-full"
              )}
            >
              <a
                target="_blank"
                href={isWebLink ? webUrl : imageUrl || ""}
                className="font-medium flex whitespace-nowrap items-center mb-0.5"
                rel="noreferrer"
                style={{ maxWidth: "100%" }}
                data-testid={`file-item-${documentId}-link`}
                title={isWebLink ? webUrl : undefined}
              >
                <FigmaTypography variant="label-2" className="truncate w-[calc(100%-5px)]" style={{ lineHeight: "120%" }}>
                  {isWebLink ? webUrl : fileName}
                </FigmaTypography>
                {status === "pending" && (
                  <FigmaTypography
                    component="span"
                    variant="body-3"
                    className="ml-1 leading-[100%] rounded bg-gray-100 px-2 py-0.5 text-muted-foreground"
                  >
                    Pending
                  </FigmaTypography>
                )}
                {status === "processing" && (
                  <span className="text-muted-foreground text-[12px] leading-[18px] rounded px-2 inline-flex align-middle justify-center">
                    <LoadingSpinner />
                  </span>
                )}
                {isSummarizing && (
                  <FigmaTypography
                    component="span"
                    variant="body-3"
                    className="ml-1 leading-[100%] rounded bg-blue-100 px-2 py-0.5 text-blue-800 flex items-center gap-1"
                  >
                    <LoadingSpinner />
                    Summarizing
                  </FigmaTypography>
                )}
              </a>
              <div className="flex flex-col gap-1">
                <FigmaTypography variant="body-3" className="text-innoveraZinc-500 w-[calc(100%-5px)] truncate">
                  {sizeField} {uploadedBy ? (isWebLink ? `Added by ${uploadedBy}` : `Uploaded by ${uploadedBy}`) : ""}
                </FigmaTypography>
                {isContentAvailable && fullContentDisplayLength > 0 && (
                  <div className="flex items-center gap-2">
                    <FigmaTypography variant="body-3" color="zinc400">
                      {contentType === "SUMMARIZED" && fullContentDisplayLength > 0
                        ? formatCharacterLength(editedSummaryContentLength)
                        : formatCharacterLength(fullContentDisplayLength)}
                    </FigmaTypography>
                    {hasSummarizedContent && contentType === "SUMMARIZED" && (
                      <FigmaTypography variant="body-3" color="zinc400">
                        ({formatCompressionRatio(fullContentDisplayLength, editedSummaryContentLength)})
                      </FigmaTypography>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="p-3 flex items-center justify-center flex-shrink-0">
            <div className="flex items-center ">
              {briefSummary && (
                <button
                  type="button"
                  onClick={() => setIsSummaryOpen((v) => !v)}
                  aria-expanded={isSummaryOpen}
                  aria-controls={summaryRegionId}
                  className="p-1 rounded transition-colors hover:bg-gray-100"
                  data-testid={`file-item-${documentId}-toggle-brief`}
                  title={isSummaryOpen ? "Hide preview" : "Show preview"}
                >
                  <EyeIcon className="w-4 h-4 text-gray-400" />
                </button>
              )}
              <button
                onClick={isLoadingDocumentsUsefulness || !hasEdit ? undefined : handleUsefulnessChange}
                disabled={isLoadingDocumentsUsefulness || !hasEdit}
                className="p-1 hover:bg-gray-100 rounded transition-colors disabled:hover:bg-transparent disabled:cursor-not-allowed"
                data-testid={`file-item-${documentId}-usefulness`}
              >
                {isLoadingDocumentsUsefulness ? (
                  <LoadingSpinner />
                ) : usefulness === TUsefulnessStatus.USEFUL ? (
                  <BookmarkCheck className="w-4 h-4 text-green-600" />
                ) : (
                  <Bookmark className="w-4 h-4 text-gray-400" />
                )}
              </button>
              <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
                <DropdownMenuTrigger disabled={isDeleting} data-testid={`file-item-${documentId}-menu-trigger`}>
                  {isDeleting ? <LoadingSpinner /> : <DotsHorizontalIcon />}
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="px-1 py-2 border-innoveraZinc-100">
                  <DropdownMenuLabel className="px-3">
                    <FigmaTypography variant="label-3" color="zinc500">
                      Actions
                    </FigmaTypography>
                  </DropdownMenuLabel>

                  {status === "processed" && hasSummarizedContent && (
                    <DropdownMenuItem
                      variant={"default"}
                      onClick={handleViewContent}
                      data-testid={`file-item-${documentId}-view-content`}
                      className="px-3 py-2 flex items-center gap-1"
                    >
                      <EyeIcon color="var(--innovera-zinc900)" />
                      <FigmaTypography variant="label-2">View Content</FigmaTypography>
                    </DropdownMenuItem>
                  )}

                  {!hasSummarizedContent && status === "processed" && onSummarize && (
                    <DropdownMenuItem
                      variant={"default"}
                      onClick={handleSummarize}
                      disabled={isSummarizing || !hasEdit}
                      data-testid={`file-item-${documentId}-summarize`}
                      className="px-3 py-2 flex items-center gap-1"
                    >
                      <SparkleIcon color="var(--innovera-zinc900)" />
                      <FigmaTypography variant="label-2">Summarize</FigmaTypography>
                    </DropdownMenuItem>
                  )}

                  {hasSummarizedContent && onSummarize && (
                    <DropdownMenuItem
                      variant={"default"}
                      onClick={handleSummarize}
                      disabled={isSummarizing || !hasEdit}
                      data-testid={`file-item-${documentId}-regenerate-summary`}
                      className="px-3 py-2 flex items-center gap-1"
                    >
                      <ArrowClockwiseIcon color="var(--innovera-zinc900)" />
                      <FigmaTypography variant="label-2">Regenerate Summary</FigmaTypography>
                    </DropdownMenuItem>
                  )}

                  {!isWebLink && (
                    <DropdownMenuItem
                      variant={"default"}
                      onClick={handleDownload}
                      disabled={isDownloading}
                      data-testid={`file-item-${documentId}-download`}
                      className="px-3 py-2 flex items-center gap-1"
                    >
                      {isDownloading ? (
                        <LoadingSpinner />
                      ) : (
                        <DownloadSimpleIcon color="var(--innovera-zinc900)" />
                      )}
                      <FigmaTypography variant="label-2">Download</FigmaTypography>
                    </DropdownMenuItem>
                  )}

                  <DropdownMenuItem
                    variant={"default"}
                    onClick={handleDelete}
                    disabled={!hasEdit}
                    data-testid={`file-item-${documentId}-delete`}
                    className="px-3 py-2 flex items-center gap-1"
                  >
                    <TrashSimpleIcon color="var(--innovera-zinc900)" />
                    <FigmaTypography variant="label-2">Delete</FigmaTypography>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
        {status === "error" && (
          <div className="flex items-center gap-2 mt-2">
            <FigmaTypography component="span" className="text-errorRed ml-1 text-[12px] leading-[18px] rounded bg-red-100 px-1 py-1">
              Error processing
            </FigmaTypography>
            <AppButton
              appearance="outlined"
              size="sm"
              className="ml-1"
              onClick={handleRetry}
              startAdornment={<ArrowClockwiseIcon />}
              data-testid={`file-item-${documentId}-retry`}
              isLoading={isRetrying}
              loadingText="Retrying..."
            >
              Retry
            </AppButton>
          </div>
        )}
        <DocumentContentModal
          isOpen={isModalOpen}
          onOpenChange={setIsModalOpen}
          fileName={fileName}
          fullContentDisplay={fullContentDisplay}
          editedSummaryContent={editedSummaryContent}
          onEditedSummaryChange={setEditedSummaryContent}
          hasSummarizedContent={hasSummarizedContent}
          onSaveContent={handleSaveContent}
          onGenerateSummary={handleGenerateSummary}
          isSavingContent={isSavingContent}
          isGeneratingSummary={isGeneratingSummary}
          fullContentDisplayLength={fullContentDisplayLength}
          editedSummaryContentLength={editedSummaryContentLength}
          readOnly={!hasEdit}
        />
      </div>

      <AnimatePresence initial={false}>
        {briefSummary && isSummaryOpen && (
          <motion.div
            key="brief-summary"
            id={summaryRegionId}
            role="region"
            aria-label={`${fileName} preview`}
            className="mt-2 overflow-hidden ml-[14px] mr-[1px] border rounded-[12px] border-innoveraZinc-100 mb-4"
            style={{
              boxShadow: "0px 2px 8px 0px #00000012"
            }}
            initial={{ height: 0, opacity: 0, y: -4 }}
            animate={{ height: "auto", opacity: 1, y: 0 }}
            exit={{ height: 0, opacity: 0, y: -4 }}
            transition={{ duration: 0.22, ease: [0.22, 0.61, 0.36, 1] }}
          >
            <div className="max-w-prose break-words bg-white p-3">
              <div className="flex flex-col gap-1">
                <div className="flex justify-between items-start w-full">
                  <FigmaTypography
                    variant="body-2"
                    className="text-innoveraZinc-900 break-words pr-2"
                    style={{ wordBreak: "break-word", flex: 1, minWidth: 0 }}
                  >
                    {isWebLink ? webUrl : fileName}
                  </FigmaTypography>
                  <button
                    type="button"
                    onClick={() => setIsSummaryOpen(false)}
                    className="p-1 rounded transition-colors hover:bg-gray-100 flex-shrink-0"
                    data-testid={`file-item-${documentId}-close-brief`}
                    title={"Hide preview"}
                    style={{ marginLeft: 4 }}
                  >
                    <XIcon className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
                {isContentAvailable && fullContentDisplayLength > 0 && (
                  <div className="flex items-center gap-2">
                    <FigmaTypography variant="body-3" color="zinc500">
                      {contentType === "SUMMARIZED"
                        ? formatCharacterLength(editedSummaryContentLength)
                        : formatCharacterLength(fullContentDisplayLength)}
                    </FigmaTypography>
                    {hasSummarizedContent && contentType === "SUMMARIZED" && (
                      <FigmaTypography variant="body-3" color="zinc400">
                        ({formatCompressionRatio(fullContentDisplayLength, editedSummaryContentLength)})
                      </FigmaTypography>
                    )}
                  </div>
                )}

                <FigmaTypography variant="body-3" className="text-innoveraZinc-600 w-[calc(100%-5px)] truncate">
                  {sizeField} {uploadedBy ? (isWebLink ? `Added by ${uploadedBy}` : `Uploaded by ${uploadedBy}`) : ""}
                </FigmaTypography>
              </div>

              <Divider className="my-[8px]" />

              <FigmaTypography variant="body-2" className="text-innoveraZinc-900">
                {briefSummary}
              </FigmaTypography>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
